/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { Button, Flex, Spinner, Text, Tooltip, Box, HStack } from '@chakra-ui/react';
import { CiCircleCheck } from 'react-icons/ci';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import { MdOutlineInsertPageBreak } from 'react-icons/md';
import { createSearchParams, Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Package, FunnelSimple, NotePencil } from 'phosphor-react';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';
import { FormBox } from '../../../../components/formBox';
import SelectInputForm from '../../../../components/inputs/forms/SelectInputForm';
import { IData, IPagination, ISelect } from '../../../../types/shared';
import { ICompany, useCompanies } from '../../../../hooks/useCompanies';
import { IClient } from '../../../../hooks/useClients';
import { formatCNPJ, getFormattedLabelString } from '../../../../utils/FormatFunctions';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { FilterDrawer } from '../../../../components/FilterDrawer';
import { Paginator } from '../../../../components/paginator';
import ViewNotaModal from '../../../../components/viewNota';
import { useCalculaPorcentagem } from '../../../../hooks/useInputCompletionPercentage';

moment.locale('pt-br');

export function ModulesHomeIntegrationSequenceBreakPage() {
  const { getQuebras, getQuebrasTotais, getCompaniesFilters } = useCompanies();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { useCalculaPorcentagemDoisArgumentos } = useCalculaPorcentagem();
  const [searchMode, setSearchMode] = useState<'ID' | 'Nome' | 'CNPJ'>('ID');
  const searchModeRef = useRef(searchMode);

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  const {
    currentDate: currentFilterDate,
    dateStart,
    dateEnd,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters,
    setFilters,
  } = useMonthFilter({ additionalParams: { search: '' } });

  // Verificar se há filtros ativos (excluindo campos básicos, ordenação e situacao)
  const hasActiveFilters = Array.from(filters.keys()).some(
    key =>
      key !== 'page' &&
      key !== 'size' &&
      key !== 'date_start' &&
      key !== 'date_end' &&
      key !== 'situacao' &&
      key !== 'order' &&
      key !== 'order_by' &&
      key !== 'search' &&
      key !== 'cnpj' &&
      filters.get(key),
  );

  // Função para limpar todos os filtros
  const handleClearFilters = () => {
    const newFilters = new URLSearchParams();
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    newFilters.set('search', filters.get('search') || '');
    if (filters.get('cnpj')) newFilters.set('cnpj', filters.get('cnpj') || '');
    newFilters.set(
      'date_start',
      filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
    );
    newFilters.set(
      'date_end',
      filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
    );
    // Mantém situacao se existir
    const situacao = filters.get('situacao');
    if (situacao) newFilters.set('situacao', situacao);
    setFilters(newFilters);
    // Limpar cada campo individualmente para forçar atualização visual
    const { setValue } = filtersFormMethods;
    setValue('companies', null as any);
    setValue('cidade', null as any);
    setValue('uf', null as any);
    setValue('grupo_de_empresas', null as any);
    setValue('tags', null as any);
    setValue('comercial', null as any);
    setValue('contabil', null as any);
    setValue('financeiro', null as any);
    setValue('contas_a_pagar', null as any);
    setValue('encerramento_anual', null as any);
    setValue('fiscal', null as any);
    setValue('imposto_de_renda', null as any);
    setValue('integracao_contabil', null as any);
    setValue('integracao_fiscal', null as any);
    setValue('pessoal', null as any);
    setValue('relacionamento_com_cliente', null as any);
    setValue('societario', null as any);
  };

  // Função para aplicar os filtros quando clica no botão "Filtrar"
  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    const newFilters = new URLSearchParams();

    // Mantém filtros básicos
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    newFilters.set('search', filters.get('search') || '');
    if (filters.get('cnpj')) newFilters.set('cnpj', filters.get('cnpj') || '');
    newFilters.set(
      'date_start',
      filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
    );
    newFilters.set(
      'date_end',
      filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
    );

    // Mantém situacao se existir
    const situacao = filters.get('situacao');
    if (situacao) newFilters.set('situacao', situacao);

    // Mantém ordenação se existir
    const orderBy = filters.get('order_by');
    const order = filters.get('order');
    if (orderBy) newFilters.set('order_by', orderBy);
    if (order) newFilters.set('order', order);

    // Aplica filtros avançados
    if (values.companies?.external_id) newFilters.set('id', values.companies.external_id);
    if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
    if (values.uf?.value) newFilters.set('uf', values.uf.value);
    if (values.grupo_de_empresas?.value)
      newFilters.set('grupo_de_empresas', values.grupo_de_empresas.value);
    if (values.tags?.value) newFilters.set('tags', values.tags.value);
    if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
    if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
    if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
    if (values.contas_a_pagar?.value) newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
    if (values.encerramento_anual?.value)
      newFilters.set('encerramento_anual', values.encerramento_anual.value);
    if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
    if (values.imposto_de_renda?.value)
      newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
    if (values.integracao_contabil?.value)
      newFilters.set('integracao_contabil', values.integracao_contabil.value);
    if (values.integracao_fiscal?.value)
      newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
    if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
    if (values.relacionamento_com_cliente?.value)
      newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
    if (values.societario?.value) newFilters.set('societario', values.societario.value);

    setFilters(newFilters);
  };

  // Função helper para criar objeto de filtro a partir do valor da URL
  const getFilterDefaultValue = (key: string): ISelect<string> | undefined => {
    const value = filters.get(key);
    return value ? { label: value, value } : undefined;
  };

  const filtersFormMethods = useForm<{
    search: string;
    cnpj: string;
    companyId: any;
    companies: ICompany;
    order_by: ISelect<string>;
    order: string;
    cliente: IClient;
    cidade: ISelect<string>;
    uf: ISelect<string>;
    grupo_de_empresas: ISelect<string>;
    tags: ISelect<string>;
    comercial: ISelect<string>;
    contabil: ISelect<string>;
    financeiro: ISelect<string>;
    contas_a_pagar: ISelect<string>;
    encerramento_anual: ISelect<string>;
    fiscal: ISelect<string>;
    imposto_de_renda: ISelect<string>;
    integracao_contabil: ISelect<string>;
    integracao_fiscal: ISelect<string>;
    pessoal: ISelect<string>;
    relacionamento_com_cliente: ISelect<string>;
    societario: ISelect<string>;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? '',
      cnpj: filters.get('cnpj') ?? '',
      cidade: getFilterDefaultValue('cidade'),
      uf: getFilterDefaultValue('uf'),
      grupo_de_empresas: getFilterDefaultValue('grupo_de_empresas'),
      tags: getFilterDefaultValue('tags'),
      comercial: getFilterDefaultValue('comercial'),
      contabil: getFilterDefaultValue('contabil'),
      financeiro: getFilterDefaultValue('financeiro'),
      contas_a_pagar: getFilterDefaultValue('contas_a_pagar'),
      encerramento_anual: getFilterDefaultValue('encerramento_anual'),
      fiscal: getFilterDefaultValue('fiscal'),
      imposto_de_renda: getFilterDefaultValue('imposto_de_renda'),
      integracao_contabil: getFilterDefaultValue('integracao_contabil'),
      integracao_fiscal: getFilterDefaultValue('integracao_fiscal'),
      pessoal: getFilterDefaultValue('pessoal'),
      relacionamento_com_cliente: getFilterDefaultValue('relacionamento_com_cliente'),
      societario: getFilterDefaultValue('societario'),
    },
  });

  // Sincronizar valores do formulário quando os filtros da URL mudarem
  useEffect(() => {
    const filterFields = [
      'cidade',
      'uf',
      'grupo_de_empresas',
      'tags',
      'comercial',
      'contabil',
      'financeiro',
      'contas_a_pagar',
      'encerramento_anual',
      'fiscal',
      'imposto_de_renda',
      'integracao_contabil',
      'integracao_fiscal',
      'pessoal',
      'relacionamento_com_cliente',
      'societario',
    ] as const;

    filterFields.forEach(field => {
      const urlValue = filters.get(field);
      if (urlValue) {
        filtersFormMethods.setValue(field, { label: urlValue, value: urlValue });
      } else {
        filtersFormMethods.setValue(field, undefined as any);
      }
    });
  }, [filters, filtersFormMethods]);

  // Debounce para o campo de busca com modo
  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    const newFilters = new URLSearchParams(filters);
    const currentMode = searchModeRef.current;

    // Limpa os parâmetros de busca anteriores
    newFilters.delete('search');
    newFilters.delete('cnpj');
    newFilters.delete('external_id');

    if (searchValue) {
      switch (currentMode) {
        case 'ID':
          newFilters.set('external_id', searchValue);
          break;
        case 'Nome':
          newFilters.set('search', searchValue);
          break;
        case 'CNPJ':
          newFilters.set('cnpj', searchValue.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
          break;
        default:
          break;
      }
    }

    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  // Função para aplicar ordenação imediatamente
  const handleOrderChange = (ordem: string | undefined) => {
    const newFilters = new URLSearchParams(filters);
    newFilters.set('page', '1');

    if (ordem === 'NFe com mais quebras') {
      newFilters.set('order', 'desc');
      newFilters.set('order_by', 'nfe');
    } else if (ordem === 'NFe com menos quebras') {
      newFilters.set('order', 'asc');
      newFilters.set('order_by', 'nfe');
    } else if (ordem === 'CTE com mais quebras') {
      newFilters.set('order', 'desc');
      newFilters.set('order_by', 'cte');
    } else if (ordem === 'CTE com menos quebras') {
      newFilters.set('order', 'asc');
      newFilters.set('order_by', 'cte');
    } else if (ordem === 'NFC-e com mais quebras') {
      newFilters.set('order', 'desc');
      newFilters.set('order_by', 'nfc');
    } else if (ordem === 'NFC-e com menos quebras') {
      newFilters.set('order', 'asc');
      newFilters.set('order_by', 'nfc');
    } else if (ordem === 'NFS-e com mais quebras') {
      newFilters.set('order', 'desc');
      newFilters.set('order_by', 'nfse');
    } else if (ordem === 'NFS-e com menos quebras') {
      newFilters.set('order', 'asc');
      newFilters.set('order_by', 'nfse');
    } else if (ordem === 'Maior total de XMLS') {
      newFilters.set('order', 'desc');
      newFilters.set('order_by', 'total');
    } else if (ordem === 'Menor total de XMLS') {
      newFilters.set('order', 'asc');
      newFilters.set('order_by', 'total');
    } else {
      newFilters.delete('order');
      newFilters.delete('order_by');
    }

    setFilters(newFilters);
  };

  // Watch no campo search para aplicar debounce
  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      // Aplica debounce no campo search
      if (name === 'search') {
        debouncedSearch(values.search || '');
      }
      // Ordenação aplica imediatamente
      if (name === 'order_by') {
        handleOrderChange(values.order_by?.value);
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch, debouncedSearch]);

  // Re-executa a busca quando o modo de busca muda
  useEffect(() => {
    debouncedSearch(filtersFormMethods.getValues().search || '');
  }, [searchMode]);

  const queryKeyCompaniesFilters = ['CompaniesFilters'];
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: queryKeyCompaniesFilters,
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  // Construir objeto de filtros a partir dos searchParams
  // Usando useMemo para garantir estabilidade do objeto de filtros
  const filtersObject = useMemo(
    () => cleanFilters(Object.fromEntries([...Array.from(filters)])),
    [filters],
  );

  // Criar filtros para totais sem situacao (para sempre mostrar valores globais nos cards)
  // mas mantendo os filtros avançados (cidade, UF, etc.)
  const filtersForTotals = useMemo(() => {
    const filtersObj = Object.fromEntries([...Array.from(filters)]);
    // Remove filtro de situação dos totais
    delete filtersObj.situacao;
    return cleanFilters(filtersObj);
  }, [filters]);

  const queryKeyQuebrasTotatais = ['getQuebrasTotais', filtersForTotals];

  const queryKeyCompany = ['getQuebras', filtersObject];

  const { data: dataCompaniesQuebras, isFetching: isFetchingCompanyQuebras } = useQuery<
    IData<ICompany[]>
  >({
    queryKey: queryKeyCompany,
    queryFn: getQuebras,
  });

  const { data: quebrasTotais, isFetching: isFetchingQuebrasTotais } = useQuery<any>({
    queryKey: queryKeyQuebrasTotatais,
    queryFn: getQuebrasTotais,
  });

  const { items: Quebras, ...pagination } = dataCompaniesQuebras?.data ?? {
    items: [],
    ...({} as IPagination),
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-col gap-3 py-5">
        <div className="flex content-start md:w-1/3 lg:content-center ">
          <ReturnButton previousPage="modulesHome/integration" />

          <div className="h-full">
            <div className="flex items-center justify-center rounded-full bg-[#DFF3FF] p-3">
              <MdOutlineInsertPageBreak color="#008CCC" className="size-8" />
            </div>
          </div>
          <div className="flex w-full flex-col justify-center gap-1 pl-2">
            <Text fontWeight="bold" fontSize="16px">
              Quebra de Sequência
            </Text>
          </div>
        </div>

        <div className="mt-4 flex flex-row flex-nowrap gap-3 overflow-x-auto">
          {isFetchingQuebrasTotais ? (
            <div className="flex flex-col items-center gap-4">
              <Spinner size="lg" color="primary" />
              <span className="text-xl font-semibold">Carregando...</span>
            </div>
          ) : (
            <div className="mb-6 mt-4 flex flex-row flex-nowrap gap-3 overflow-x-auto">
              {/* Card Empresas NFE */}
              <Tooltip
                label="Empresas com quebras de sequência em NFE: Indica quantidade de empresas que possuem quebras na numeração de Notas Fiscais Eletrônicas"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'nfe' ? 'bg-[#DCFAF8]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'nfe') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'nfe');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCFAF8] p-2">
                    <Package color="#16DBCC" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Empresas NFE
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.empresas_nfe ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.empresas_nfe
                        ? `${useCalculaPorcentagemDoisArgumentos(quebrasTotais?.empresas_nfe, quebrasTotais?.qnt_empresas)}%`
                        : '0%'}
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Empresas NFCE */}
              <Tooltip
                label="Empresas com quebras de sequência em NFCE: Indica quantidade de empresas que possuem quebras na numeração de Notas Fiscais de Consumidor Eletrônicas"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'nfce' ? 'bg-[#FFE0EB]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'nfce') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'nfce');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE0EB] p-2">
                    <Package color="#FF82AC" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Empresas NFCE
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.empresas_nfce ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.empresas_nfce
                        ? `${useCalculaPorcentagemDoisArgumentos(quebrasTotais?.empresas_nfce, quebrasTotais?.qnt_empresas)}%`
                        : '0%'}
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Empresas NFSE */}
              <Tooltip
                label="Empresas com quebras de sequência em NFSE: Indica quantidade de empresas que possuem quebras na numeração de Notas Fiscais de Serviço Eletrônicas"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'nfse' ? 'bg-[#FFF4E6]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'nfse') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'nfse');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF4E6] p-2">
                    <Package color="#F59E0B" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Empresas NFSE
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.empresas_nfse ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.empresas_nfse
                        ? `${useCalculaPorcentagemDoisArgumentos(quebrasTotais?.empresas_nfse, quebrasTotais?.qnt_empresas)}%`
                        : '0%'}
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Empresas CTE */}
              <Tooltip
                label="Empresas com quebras de sequência em CTE: Indica quantidade de empresas que possuem quebras na numeração de Conhecimentos de Transporte Eletrônicos"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'cte' ? 'bg-orange-100' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'cte') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'cte');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 p-2">
                    <Package color="#EA580C" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Empresas CTE
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.empresas_cte ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.empresas_cte
                        ? `${useCalculaPorcentagemDoisArgumentos(quebrasTotais?.empresas_cte, quebrasTotais?.qnt_empresas)}%`
                        : '0%'}
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Sem Quebras */}
              <Tooltip
                label="Sem quebra de sequência: Empresas que não possuem nenhuma quebra na numeração de notas fiscais"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'ok' ? 'bg-[#DCFAF8]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'ok') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'ok');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCFAF8] p-2">
                    <CiCircleCheck color="#16DBCC" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Sem Quebras
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.sem_quebras ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.sem_quebras
                        ? `${useCalculaPorcentagemDoisArgumentos(quebrasTotais?.sem_quebras, quebrasTotais?.qnt_empresas)}%`
                        : '0%'}
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Com Quebras */}
              <Tooltip
                label="Com quebra de sequência: Total de empresas que possuem quebras na numeração de notas fiscais"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'erro' ? 'bg-[#FFE0EB]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'erro') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'erro');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE0EB] p-2">
                    <IoCloseCircleOutline color="#FF82AC" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Com Quebras
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.quebras ?? 0}
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Outros */}
              <Tooltip
                label="Outros: Empresas com situações especiais ou em análise que não se encaixam nas categorias anteriores"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'outro' ? 'bg-gray-200' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'outro') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'outro');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 p-2">
                    <Package color="#4b5563" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Outros
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.outros ?? 0}
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Total de Empresas */}
              <Tooltip
                label="Total de Empresas: Quantidade total de empresas no sistema para o período selecionado"
                placement="top"
              >
                <Flex className="flex items-center gap-2 rounded-2xl bg-blue-50 p-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 p-2">
                    <Package color="#3B82F6" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Total Empresas
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.qnt_empresas ?? 0}
                    </Text>
                  </div>
                </Flex>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      {/* Filtros no topo */}
      <Box mb={6} p={4} bg="gray.50" borderRadius="lg">
        <FormProvider {...filtersFormMethods}>
          {/* Linha principal: Pesquisa + Navegação de Mês + Botão Filtros */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            gap={4}
          >
            {/* Campo de Pesquisa com Seletor de Modo */}
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              gap={2}
              flex={1}
              maxW={{ base: '100%', md: '400px' }}
              align={{ base: 'stretch', sm: 'center' }}
            >
              <SearchModeBar
                modes={MODES_ID_NOME_CNPJ}
                mode={searchMode}
                onModeChange={m => setSearchMode(m as typeof searchMode)}
                value={filtersFormMethods.watch('search') ?? ''}
                onValueChange={v => filtersFormMethods.setValue('search', v, { shouldDirty: true })}
              />
            </Flex>

            {/* Ordenação, Navegação de Mês e Botão Filtros */}
            <HStack spacing={3} flexWrap="wrap" align="center">
              <Box minW={{ base: '100%', md: '220px' }}>
                <SelectInputForm<ISelect<string>>
                  name="order_by"
                  options={[
                    { label: 'NFe com mais quebras', value: 'NFe com mais quebras' },
                    { label: 'NFe com menos quebras', value: 'NFe com menos quebras' },
                    { label: 'CTE com mais quebras', value: 'CTE com mais quebras' },
                    { label: 'CTE com menos quebras', value: 'CTE com menos quebras' },
                    { label: 'NFC-e com mais quebras', value: 'NFC-e com mais quebras' },
                    { label: 'NFC-e com menos quebras', value: 'NFC-e com menos quebras' },
                    { label: 'NFS-e com mais quebras', value: 'NFS-e com mais quebras' },
                    { label: 'NFS-e com menos quebras', value: 'NFS-e com menos quebras' },
                    { label: 'Maior total de XMLS', value: 'Maior total de XMLS' },
                    { label: 'Menor total de XMLS', value: 'Menor total de XMLS' },
                  ]}
                  placeholder="Ordenar por"
                  controlMb={0}
                  chakraStyles={{
                    control: styles => ({
                      ...styles,
                      minH: '38px',
                      height: '38px',
                    }),
                  }}
                />
              </Box>
              <MonthNavigator
                currentDate={currentFilterDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                onSelectMonth={handleSelectMonth}
              />
              <Button
                onClick={() => setIsFilterDrawerOpen(true)}
                leftIcon={<FunnelSimple size={16} />}
                variant="outline"
                colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                height="38px"
              >
                Filtros{' '}
                {hasActiveFilters &&
                  `(${Array.from(filters.keys()).filter(key => key !== 'page' && key !== 'size' && key !== 'date_start' && key !== 'date_end' && key !== 'situacao' && key !== 'order' && key !== 'order_by' && key !== 'search' && key !== 'cnpj' && filters.get(key)).length})`}
              </Button>
            </HStack>
          </Flex>

          {/* FilterDrawer para filtros avançados */}
          <FilterDrawer
            isOpen={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            title="Filtros Avançados"
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            onApplyFilters={handleApplyFilters}
            formMethods={filtersFormMethods}
            size="lg"
          >
            <div className="grid grid-cols-2 gap-4">
              <SelectInputForm<ISelect<string>>
                name="cidade"
                placeholder="Filtrar por cidade"
                options={getFormattedLabelString(CompaniesFilters?.cidade)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="uf"
                placeholder="Filtrar por Estado"
                options={getFormattedLabelString(CompaniesFilters?.uf)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="grupo_de_empresas"
                placeholder="Filtrar por grupo de empresa"
                options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="tags"
                placeholder="Filtrar por tags"
                options={getFormattedLabelString(CompaniesFilters?.tags)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="comercial"
                placeholder="Filtrar por comercial"
                options={getFormattedLabelString(CompaniesFilters?.comercial)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="contabil"
                placeholder="Filtrar pelo contábil"
                options={getFormattedLabelString(CompaniesFilters?.contabil)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="financeiro"
                placeholder="Filtrar pelo financeiro"
                options={getFormattedLabelString(CompaniesFilters?.financeiro)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="contas_a_pagar"
                placeholder="Filtrar por contas à pagar"
                options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="encerramento_anual"
                placeholder="Filtrar por encerramento anual"
                options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="fiscal"
                placeholder="Filtrar por fiscal"
                options={getFormattedLabelString(CompaniesFilters?.fiscal)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="imposto_de_renda"
                placeholder="filtrar por imposto de renda"
                options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="integracao_contabil"
                placeholder="Filtrar por integração contabil"
                options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="integracao_fiscal"
                placeholder="Filtrar por integração fiscal"
                options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="pessoal"
                placeholder="Filtrar por pessoal"
                options={getFormattedLabelString(CompaniesFilters?.pessoal)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="relacionamento_com_cliente"
                placeholder="Filtrar por relacionamento com cliente"
                options={getFormattedLabelString(CompaniesFilters?.relacionamento_com_cliente)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="societario"
                placeholder="Filtrar por societário"
                options={getFormattedLabelString(CompaniesFilters?.societario)}
                controlMb={0}
              />
            </div>
          </FilterDrawer>
        </FormProvider>
      </Box>

      <FormBox>
        <div className="flex flex-col gap-4 p-4">
          {isFetchingCompanyQuebras ? (
            <div className="flex flex-col items-center gap-4">
              <Spinner size="lg" color="primary" />
              <span className="text-xl font-semibold">Carregando...</span>
            </div>
          ) : Quebras?.length === 0 ? (
            <div className="flex flex-col items-center gap-4">Nenhum item encontrado</div>
          ) : filters.get('search') && Quebras?.length === 0 && !isFetchingCompanyQuebras ? (
            <div className="flex flex-col items-center gap-4">Nenhum item encontrado</div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {Quebras?.map(item => (
                  <div
                    key={item.id}
                    className="relative rounded border border-gray-300 bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
                  >
                    {/* Botões de Ação - Canto Superior Direito */}
                    <div className="absolute right-2 top-2 flex gap-2">
                      <ViewNotaModal
                        screen="quebra_sequencia"
                        id={item?.id}
                        dateStart={dateStart}
                        dateEnd={dateEnd}
                        defaultTab="quebra_sequencia"
                        companyName={item?.razao_social}
                      >
                        <Button
                          variant="outline"
                          size="xs"
                          borderRadius="full"
                          borderColor="#718EBF"
                          bg="white"
                          p={1.5}
                          minW="auto"
                        >
                          <NotePencil size={14} color="#718EBF" />
                        </Button>
                      </ViewNotaModal>
                      <Link
                        to={{
                          pathname: `/breaks/${item.external_id}`,
                          search: createSearchParams({
                            date_start:
                              dateStart || moment().subtract(6, 'days').format('YYYY-MM-DD'),
                            date_end: dateEnd || moment().format('YYYY-MM-DD'),
                          }).toString(),
                        }}
                        state={{ company: item }}
                      >
                        <Button
                          variant="outline"
                          size="xs"
                          borderRadius="full"
                          borderColor="#718EBF"
                          bg="white"
                          p={1.5}
                          minW="auto"
                        >
                          <Text fontSize="xs" color="#718EBF">
                            Quebras
                          </Text>
                        </Button>
                      </Link>
                    </div>

                    <div className="flex w-full flex-col gap-2">
                      {/* Header: ID + Nome + CNPJ */}
                      <div className="flex flex-col gap-1 pr-24">
                        <Text fontWeight="bold" fontSize="sm">
                          ID: {item.external_id}
                        </Text>
                        <Tooltip label={item.razao_social} placement="top">
                          <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                            {item.razao_social}
                          </Text>
                        </Tooltip>
                        <Text fontSize="xs" color="gray.600">
                          {formatCNPJ(item.cnpj)}
                        </Text>
                      </div>

                      {/* Cards de Quebras */}
                      <div className="grid grid-cols-2 gap-2">
                        {/* NFE */}
                        <div className="rounded border border-blue-200 bg-blue-50 p-2">
                          <Text fontSize="xs" fontWeight="bold" color="blue.600" mb={1}>
                            NFE
                          </Text>
                          <div className="flex items-center gap-2">
                            {item?.nf_quebradas?.nfe === 0 ? (
                              <CiCircleCheck color="#16DBCC" className="size-5" />
                            ) : (
                              <>
                                <IoCloseCircleOutline color="#FF82AC" className="size-5" />
                                <Text fontSize="sm" fontWeight="bold">
                                  {item?.nf_quebradas?.nfe}
                                </Text>
                              </>
                            )}
                          </div>
                        </div>

                        {/* NFCE */}
                        <div className="rounded border border-purple-200 bg-purple-50 p-2">
                          <Text fontSize="xs" fontWeight="bold" color="purple.600" mb={1}>
                            NFCE
                          </Text>
                          <div className="flex items-center gap-2">
                            {item?.nf_quebradas?.nfce === 0 ? (
                              <CiCircleCheck color="#16DBCC" className="size-5" />
                            ) : (
                              <>
                                <IoCloseCircleOutline color="#FF82AC" className="size-5" />
                                <Text fontSize="sm" fontWeight="bold">
                                  {item?.nf_quebradas?.nfce}
                                </Text>
                              </>
                            )}
                          </div>
                        </div>

                        {/* NFSE */}
                        <div className="rounded border border-green-200 bg-green-50 p-2">
                          <Text fontSize="xs" fontWeight="bold" color="green.600" mb={1}>
                            NFSE
                          </Text>
                          <div className="flex items-center gap-2">
                            {item?.nf_quebradas?.nfse === 0 ? (
                              <CiCircleCheck color="#16DBCC" className="size-5" />
                            ) : (
                              <>
                                <IoCloseCircleOutline color="#FF82AC" className="size-5" />
                                <Text fontSize="sm" fontWeight="bold">
                                  {item?.nf_quebradas?.nfse}
                                </Text>
                              </>
                            )}
                          </div>
                        </div>

                        {/* CTE */}
                        <div className="rounded border border-orange-200 bg-orange-50 p-2">
                          <Text fontSize="xs" fontWeight="bold" color="orange.600" mb={1}>
                            CTE
                          </Text>
                          <div className="flex items-center gap-2">
                            {item?.nf_quebradas?.cte === 0 ? (
                              <CiCircleCheck color="#16DBCC" className="size-5" />
                            ) : (
                              <>
                                <IoCloseCircleOutline color="#FF82AC" className="size-5" />
                                <Text fontSize="sm" fontWeight="bold">
                                  {item?.nf_quebradas?.cte}
                                </Text>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Total de XMLs */}
                      <div className="flex justify-between rounded bg-gray-50 p-2">
                        <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                          Total de XMLs:
                        </Text>
                        <Text fontSize="xs" fontWeight="bold" color="gray.900">
                          {(item?.quebras ?? 0) + (item?.sem_quebras ?? 0)}
                        </Text>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Paginator pagination={pagination} />
            </>
          )}
        </div>
      </FormBox>
    </div>
  );
}
